import { useId, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Loader2, MessageSquareText } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { sendCandidateFeedback } from "@/lib/api";
import {
  CANDIDATE_FEEDBACK_MAX,
  classifyFeedbackError,
  feedbackCanSend,
  feedbackErrorMessage,
  normalizeFeedback,
  type FeedbackFailure,
} from "@/lib/candidateFeedback";
import { cn } from "@/lib/utils";

/**
 * CP-20 — the optional feedback box under the thank-you card. Rendered by the
 * Done route only when `/submit` said the recruiter turned it on for this test
 * AND the attempt is submitted; never by the recruiter's Settings preview
 * (which renders `CandidateDoneCard` alone — this card is deliberately a
 * sibling, not a slot, so that preview cannot drift into showing a form).
 *
 * Three quiet states, one form:
 *   - `idle`    the textarea + Send — the only state with a control
 *   - `sent`    "Thanks for your feedback." — also the INITIAL state when
 *               `/submit` reported the feedback as already given (a refresh),
 *               so nothing about "sent" lives in sessionStorage
 *   - `closed`  the recruiter switched feedback off while this screen was
 *               open (the flag is read live) — rendered calmly, not as an error
 * Only a genuine failure (network, 5xx, rate limit) gets the alert box, and the
 * text stays in the box so a retry costs the candidate nothing.
 */
export function CandidateFeedbackCard({
  token,
  attemptId,
  initiallyGiven,
  className,
}: {
  token: string;
  attemptId: string;
  initiallyGiven: boolean;
  className?: string;
}) {
  const fieldId = useId();
  const hintId = useId();
  const [text, setText] = useState("");
  const [phase, setPhase] = useState<"idle" | "sent" | "closed">(
    initiallyGiven ? "sent" : "idle",
  );
  const [failure, setFailure] = useState<FeedbackFailure | null>(null);

  const mutation = useMutation({
    mutationFn: (body: string) => sendCandidateFeedback(token, attemptId, body),
    onMutate: () => setFailure(null),
    onSuccess: () => setPhase("sent"),
    onError: (err) => {
      const kind = classifyFeedbackError(err);
      // Not failures from the candidate's side — the first text is safe, or
      // the box was closed under them. Settle the card instead of alarming.
      if (kind === "already_given") setPhase("sent");
      else if (kind === "closed") setPhase("closed");
      else setFailure(kind);
    },
  });

  if (phase !== "idle") {
    return (
      <Card className={cn("w-full max-w-lg", className)}>
        <CardContent
          role="status"
          className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground"
        >
          {phase === "sent" ? (
            <>
              <CheckCircle2
                className="h-4 w-4 shrink-0 text-success"
                aria-hidden="true"
              />
              Thanks for your feedback.
            </>
          ) : (
            <>
              <MessageSquareText className="h-4 w-4 shrink-0" aria-hidden="true" />
              Feedback is closed for this test.
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  const canSend = feedbackCanSend(text, mutation.isPending);

  return (
    <Card className={cn("w-full max-w-lg", className)}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const body = normalizeFeedback(text);
          if (body !== null && !mutation.isPending) mutation.mutate(body);
        }}
      >
        <CardHeader className="space-y-1 pb-3">
          <div className="flex items-center gap-2">
            <MessageSquareText
              className="h-4 w-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <CardTitle className="text-base">How was the test?</CardTitle>
          </div>
          <CardDescription id={hintId}>
            Optional. The recruiter&rsquo;s team reads this alongside your
            results &mdash; it doesn&rsquo;t affect your score.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <label htmlFor={fieldId} className="sr-only">
              Your feedback
            </label>
            <textarea
              id={fieldId}
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={CANDIDATE_FEEDBACK_MAX}
              disabled={mutation.isPending}
              rows={4}
              placeholder="What went well? What would you change?"
              aria-describedby={hintId}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
            <div className="flex justify-end font-mono text-xs text-muted-foreground">
              {text.length} / {CANDIDATE_FEEDBACK_MAX}
            </div>
          </div>
          {failure !== null && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {feedbackErrorMessage(failure)}
            </div>
          )}
          <div className="flex justify-end">
            <Button type="submit" disabled={!canSend} className="cursor-pointer">
              {mutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Sending…
                </>
              ) : (
                "Send feedback"
              )}
            </Button>
          </div>
        </CardContent>
      </form>
    </Card>
  );
}
